import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Header from "@/components/Header";
import AccessibilityToolbar from "@/components/AccessibilityToolbar";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import CurriculumLibrary from "@/pages/CurriculumLibrary";
import TeacherDashboard from "@/pages/TeacherDashboard";
import StudentDashboard from "@/pages/StudentDashboard";
import ParentDashboard from "@/pages/ParentDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import PlansPage from "@/pages/PlansPage";
import RubricsPage from "@/pages/RubricsPage";
import MessagesPage from "@/pages/MessagesPage";
import TranslationsAdminPage from "@/pages/TranslationsAdminPage";
import AIStudioPage from "@/pages/AIStudioPage";
import ResourceLibraryPage from "@/pages/ResourceLibraryPage";
import { StudentPlansListPage, StudentPlanDetailPage, IEPCertificatesPage } from "@/pages/StudentPlansPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminCentersPage from "@/pages/AdminCentersPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AdminAuditPage from "@/pages/AdminAuditPage";
import ExerciseLibraryPage from "@/pages/ExerciseLibraryPage";
import ExerciseDetailPage from "@/pages/ExerciseDetailPage";
import ExerciseGeneratorPage from "@/pages/ExerciseGeneratorPage";
import VideoAnalysisPage from "@/pages/VideoAnalysisPage";
import { ParalympicAcademyPage, SpecialOlympicsAcademyPage } from "@/pages/AcademyPages";
import AcademyHub from "@/pages/AcademyHub";
import TalentAssessment from "@/pages/TalentAssessment";
import TrainingPrograms from "@/pages/TrainingPrograms";
import ClassificationCenter from "@/pages/ClassificationCenter";
import VideoLibraryPage from "@/pages/VideoLibraryPage";

function Protected({ children, roles }) {
  const { user } = useApp();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/curriculum" element={<CurriculumLibrary />} />
        <Route path="/teacher" element={<Protected roles={['teacher']}><TeacherDashboard /></Protected>} />
        <Route path="/plans" element={<Protected roles={['teacher', 'admin']}><PlansPage /></Protected>} />
        <Route path="/rubrics" element={<Protected roles={['teacher', 'admin']}><RubricsPage /></Protected>} />
        <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
        <Route path="/ai-studio" element={<Protected><AIStudioPage /></Protected>} />
        <Route path="/video-library" element={<Protected><VideoLibraryPage /></Protected>} />
        <Route path="/library" element={<Protected><ResourceLibraryPage /></Protected>} />
        <Route path="/student-plans" element={<Protected roles={['teacher', 'admin']}><StudentPlansListPage /></Protected>} />
        <Route path="/exercises" element={<Protected><ExerciseLibraryPage /></Protected>} />
        <Route path="/exercises/generate" element={<Protected roles={['teacher', 'admin']}><ExerciseGeneratorPage /></Protected>} />
        <Route path="/exercises/analyze" element={<Protected roles={['teacher', 'admin']}><VideoAnalysisPage /></Protected>} />
        <Route path="/exercises/:id" element={<Protected><ExerciseDetailPage /></Protected>} />
        <Route path="/academy/paralympic" element={<ParalympicAcademyPage />} />
        <Route path="/academy/special-olympics" element={<SpecialOlympicsAcademyPage />} />
        <Route path="/academy/:academy/hub" element={<AcademyHub />} />
        <Route path="/academy/talent-assessment" element={<TalentAssessment />} />
        <Route path="/academy/training" element={<Protected><TrainingPrograms /></Protected>} />
        <Route path="/academy/classification" element={<Protected><ClassificationCenter /></Protected>} />
        <Route path="/student-plans/:id" element={<Protected roles={['teacher', 'admin', 'parent', 'student']}><StudentPlanDetailPage /></Protected>} />
        <Route path="/student-plans/:id/certificates" element={<Protected roles={['teacher', 'admin', 'parent', 'student']}><IEPCertificatesPage /></Protected>} />
        <Route path="/admin/translations" element={<Protected roles={['admin']}><TranslationsAdminPage /></Protected>} />
        <Route path="/admin/users" element={<Protected roles={['admin']}><AdminUsersPage /></Protected>} />
        <Route path="/admin/centers" element={<Protected roles={['admin']}><AdminCentersPage /></Protected>} />
        <Route path="/admin/analytics" element={<Protected roles={['admin']}><AdminAnalyticsPage /></Protected>} />
        <Route path="/admin/audit" element={<Protected roles={['admin']}><AdminAuditPage /></Protected>} />
        <Route path="/student" element={<Protected roles={['student']}><StudentDashboard /></Protected>} />
        <Route path="/parent" element={<Protected roles={['parent']}><ParentDashboard /></Protected>} />
        <Route path="/admin" element={<Protected roles={['admin']}><AdminDashboard /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <AccessibilityToolbar />
      <Toaster richColors position="top-center" />
    </>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
